/*
  Partner-created records awaiting the customer's approval.

  A partner can add an asset or a beneficiary to a customer's account, and until
  the customer approves it that record does nothing at all. Not "is hidden" --
  does nothing: it is not counted by the inheritance mechanism, it raises no
  attention point, it grants nobody access, and it receives nothing on D-day.
  The only place it appears is the customer's own screen, marked for review.

  That rule is only as good as the number of places it is written down, so it is
  written down once, here. Anything reading a customer's assets or beneficiaries
  to decide what the mechanism does should read them through `active()`.
*/

export const isPendingApproval = (record) =>
  Boolean(record) && record.pending_approval === true;

/*
  The list with the drafts taken out -- the list every calculation wants.
*/
export const active = (records) =>
  Array.isArray(records) ? records.filter((r) => !isPendingApproval(r)) : [];

/*
  The drafts on their own, for the review surfaces that are the one exception.
*/
export const pending = (records) =>
  Array.isArray(records) ? records.filter(isPendingApproval) : [];

/*
  Whether the viewer may change this record.

  Always true for a customer looking at their own account. For a partner it is
  true for exactly one thing: a record they created themselves that the customer
  has not approved yet. Everything else the customer wrote is theirs alone --
  the partner can read what was shared and nothing more.

  This is the one place the rule lives, because it decides both whether the edit
  control appears and whether the modal behind it can save.
*/
export const canEditRecord = (userData, record) => {
  if (!userData?.partnerReadOnly) return true;
  return (
    isPendingApproval(record) &&
    Boolean(record?.created_by_partner) &&
    record.created_by_partner === userData?.CustomerUser?.user_id
  );
};

/*
  A record a partner has asked to CHANGE, as opposed to one they created.

  The two are different events with the same answer: something on this record
  is waiting for the customer, and until they answer it the record carries the
  same purple and the same Review marker. A reader does not need to know which
  kind of waiting it is before deciding to look.

  `needsReview` is the one both tables ask. Keep them asking it rather than
  either flag, so a third kind of pending -- if there ever is one -- lands in
  one place.
*/
export const hasProposedChanges = (record) =>
  Array.isArray(record?.partner_changes) && record.partner_changes.length > 0;

export const needsReview = (record) =>
  isPendingApproval(record) || hasProposedChanges(record);

export const hasPending = (userData) =>
  pending(userData?.assets).length > 0 ||
  pending(userData?.beneficiaries).length > 0 ||
  pending(userData?.trustees).length > 0;
